import _ from "lodash";
import Promise from "bluebird";
import apiCore from "services/apiCore";
import log from "services/loggingService";

const SETTINGS_ROUTE = "/settings";

const userSettingsService = {
    /**
     * Get the settings stored for the current user. If a list of keys is provided to
     * this method, only those settings will be returned instead of the entire
     * collection.
     *
     * @param keys, ... {String}  [Optional] This method's arguments (a comma
     *                            separated list of strings) will be used as a filter
     *                            to return a subset of the settings stored for the
     *                            current user.
     *
     * @return {Promise} A promise that will pass the map of settings stored for the
     *                   current user when it resolves.
     */
    getSettings: function () {
        const args = arguments;
        return apiCore.v2
            .get(SETTINGS_ROUTE)
            .then(function (response) {
                let settings = null;

                if (args.length > 0) {
                    // If the user has provided a list of settings keys, create an object
                    // that only contains those settings.
                    _.forOwn(response, function (value, key) {
                        if (_.indexOf(args, key) !== -1) {
                            if (!settings) {
                                settings = {};
                            }

                            settings[key] = value;
                        }
                    });

                    return settings;
                }

                return response;
            })
            .catch(function (err) {
                // log an error here since it this service is mostly used to
                // configure app settings that are "invisible" to the end user.
                //
                // There is no reason to display an error in the UI when a request
                // to this service fails.
                log.error("Failed to retrieve user settings", err);
            });
    },

    /**
     * Set a collection of settings for the current user. The values passed into this
     * function will update the current settings with only what has changed. For
     * example, if you have the current settings stored for the user:
     *
     *     { "foo": "bar" }
     *
     * And you pass in the following to this method:
     *
     *     { "chalupa": "batman" }
     *
     * The result will be:
     *
     *     { "foo": "bar", "chalupa": "batman" }
     *
     * @param settings {Object} A map of key-value pairs to store for the current user
     *                          and persist across sessions.
     *
     * @return {Promise} A promise that will pass the new map of settings stored for
     *                   the current user when it resolves.
     */
    setSettings: function (settings) {
        return apiCore.v2.put(SETTINGS_ROUTE, settings).catch(function (err) {
            log.error("Failed to set the user settings", err);
        });
    },
};

export default userSettingsService;
